/* WakeUpWeeklyReportCard — pet shows your SOS patterns for the week.
 * Encourages reflection without judgment. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendingDown, TrendingUp, Minus, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_META = {
  better:      { emoji: '✨', label: 'Bättre',         color: '#B9FBC0' },
  coffee_nap:  { emoji: '☕', label: 'Kaffe-tupplur',   color: '#FFE8A3' },
  took_break:  { emoji: '🌙', label: 'Paus',            color: '#A2D2FF' },
  still_foggy: { emoji: '🌫️', label: 'Fortsatt dimma', color: '#CDB4DB' },
  pending:     { emoji: '⏳', label: 'Pågående',        color: '#E0E0E0' },
};

export default function WakeUpWeeklyReportCard() {
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wakeup-sos/stats/weekly`, { withCredentials: true });
      setStats(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!stats || stats.all_time_total < 2) return null;

  const trendIcon = stats.trend === 'better' ? <TrendingDown className="w-4 h-4 text-green-600" />
                  : stats.trend === 'worse'  ? <TrendingUp className="w-4 h-4 text-pink-600" />
                  : <Minus className="w-4 h-4 opacity-60" />;
  const maxCount = Math.max(1, ...stats.timeline.map((d) => d.count));

  return (
    <div className="neo-card p-3 bg-white mb-4" data-testid="sos-weekly-report">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2"
        data-testid="report-toggle">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 className="w-4 h-4 text-[#D06892] shrink-0" />
          <div className="text-left min-w-0">
            <p className="text-[10px] font-bold uppercase opacity-60 tracking-wide">Veckorapport — Vakna upp</p>
            <p className="font-bold text-sm truncate">
              {stats.this_week_count} {stats.this_week_count === 1 ? 'session' : 'sessioner'} denna vecka
              {trendIcon && <span className="ml-1 inline-flex align-middle">{trendIcon}</span>}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      <p className="text-xs opacity-70 italic mt-1 pl-6">🐾 {stats.trend_message}</p>

      {expanded && (
        <div className="mt-3 space-y-3" data-testid="report-expanded">
          {/* 7-day bar chart */}
          <div>
            <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Veckan i översikt</p>
            <div className="flex items-end gap-1 h-20 bg-[#FFF8FB] rounded-md p-2" data-testid="report-timeline">
              {stats.timeline.map((d, i) => {
                const h = Math.max(4, (d.count / maxCount) * 60);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="text-[9px] font-bold opacity-70">{d.count || ''}</div>
                    <div className="w-full rounded-t transition-all"
                      style={{ height: `${h}px`, background: d.count ? 'linear-gradient(to top, #D06892, #FFC8DD)' : '#EAEAEA' }} />
                    <div className="text-[9px] opacity-60">{d.weekday}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top patterns */}
          <div className="grid grid-cols-2 gap-2">
            {stats.top_weekday && (
              <div className="neo-card p-2 bg-[#FFE8F5] text-xs" data-testid="report-top-weekday">
                <p className="text-[9px] uppercase opacity-60 font-bold">Mest dimmig dag</p>
                <p className="font-bold text-sm">{stats.top_weekday.name}</p>
              </div>
            )}
            {stats.top_hour !== null && (
              <div className="neo-card p-2 bg-[#FFE8A3] text-xs" data-testid="report-top-hour">
                <p className="text-[9px] uppercase opacity-60 font-bold">Mest dimmig tid</p>
                <p className="font-bold text-sm">kl {String(stats.top_hour).padStart(2, '0')}:00</p>
              </div>
            )}
          </div>

          {/* Outcome breakdown */}
          {stats.this_week_count > 0 && (
            <div data-testid="report-outcomes">
              <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Vad funkade?</p>
              <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-gray-100">
                {Object.entries(stats.outcomes).map(([k, v]) => {
                  if (!v) return null;
                  const pct = (v / stats.this_week_count) * 100;
                  const meta = OUTCOME_META[k];
                  return (
                    <div key={k}
                      style={{ width: `${pct}%`, background: meta?.color || '#999' }}
                      title={`${meta?.label}: ${v}`}
                      data-testid={`report-outcome-bar-${k}`} />
                  );
                })}
              </div>
              <div className="flex gap-2 flex-wrap mt-1">
                {Object.entries(stats.outcomes).map(([k, v]) => {
                  if (!v) return null;
                  const meta = OUTCOME_META[k];
                  return (
                    <span key={k} className="text-[10px] flex items-center gap-1" data-testid={`report-outcome-${k}`}>
                      <span className="w-2 h-2 rounded-full" style={{ background: meta?.color }} />
                      <span>{meta?.emoji} {meta?.label}: <b>{v}</b></span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[10px] opacity-50 italic text-center pt-1">
            Totalt {stats.all_time_total} sessioner sedan start · Förra veckan: {stats.last_week_count}
          </p>
        </div>
      )}
    </div>
  );
}
